import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import Hotjar from "@hotjar/browser";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TranslationsProvider} from "@kamstrup/flow-core/localization";
import { routes } from "./routes";
import { AuthProvider, useAuth } from "@kamstrup/flow-core/security";
import { AuthErrorComponent } from "./error/authError";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getEnv } from "src/config/index";
import { getServices } from "src/logs";
import "./index.css";
import { SUPPORTED_LOCALES, toKey } from "src/localization/locales.config";
import developerTranslationFile from "src/localization/i18n/dashboardTranslation.json";
import { createDSTheme, initMUILicense } from "@kamstrup/ds-tokens";
import "@kamstrup/ds-tokens/styles/variables.css";

getEnv();
initMUILicense()
const { logger } = getServices();
const queryClient = new QueryClient();

const authConfig = {
  auth: {
    clientId: getEnv().REACT_APP_CLIENT_ID || "",
    redirectUri: getEnv().REACT_APP_REDIRECT_URI || "",
    authority: getEnv().REACT_APP_AUTHORITY || "",
    knownAuthorities: [getEnv().REACT_APP_AUTHORITY || ""],
    logoutRedirectUri: getEnv().REACT_APP_LOGOUT_URI || ""
  },
  scopes: []
};

if ((getEnv().REACT_APP_ENVIRONMENT || "").toLowerCase() === "development") {
  console.log("Auth Config:");
  console.table({
    REACT_APP_ANALYTICS_ACCESS_TOKEN_SCOPE: getEnv().REACT_APP_ANALYTICS_ACCESS_TOKEN_SCOPE,
    REACT_APP_FLOW_ACCESS_TOKEN_SCOPE: getEnv().REACT_APP_FLOW_ACCESS_TOKEN_SCOPE,
    REACT_APP_CLIENT_ID: getEnv().REACT_APP_CLIENT_ID,
    REACT_APP_REDIRECT_URI: getEnv().REACT_APP_REDIRECT_URI,
    REACT_APP_LOGOUT_URI: getEnv().REACT_APP_LOGOUT_URI,
    REACT_APP_AUTHORITY: getEnv().REACT_APP_AUTHORITY,
    PUBLIC_URL: getEnv().PUBLIC_URL,
    REACT_APP_BUILD_VERSION: getEnv().REACT_APP_BUILD_VERSION,
    REACT_APP_BE_REFRESH_INTERVAL: getEnv().REACT_APP_BE_REFRESH_INTERVAL,
    REACT_APP_AI_APPNAME: getEnv().REACT_APP_AI_APPNAME,
    REACT_APP_AI_INSTRUMENTATION_KEY: getEnv().REACT_APP_AI_INSTRUMENTATION_KEY,
    REACT_APP_BACKEND_URL: getEnv().REACT_APP_BACKEND_URL
  });
}

// eslint-disable-next-line @typescript-eslint/no-require-imports
const translations = Object.fromEntries(
  SUPPORTED_LOCALES.map(({ folder }) => {
    try {
      return [toKey(folder), require(`src/localization/i18n/FromCrowdin/${folder}/dashboardTranslation.json`)];
    } catch (error) {
      throw new Error(`Failed to load translation file for locale '${folder}': ${error instanceof Error ? error.message : String(error)}`);
    }
  }),
);

const DashboardModule = () => {
  const environment = (getEnv().REACT_APP_ENVIRONMENT || "").toLowerCase();
  const auth = useAuth();
  const idTokenClaims = auth.instance.getActiveAccount()?.idTokenClaims;
 
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = useMemo(() => createDSTheme(prefersDark ? "dark" : "light"), [prefersDark]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", prefersDark ? "dark" : "light");
  }, [prefersDark]);

  useEffect(() => {
    if (idTokenClaims) {
      let siteId;
      if (environment === "production") siteId = 5004633;
      else if (environment === "test") siteId = 3859914;
      if (siteId) {
        try {
          Hotjar.init(siteId, 6);
        } catch (error) {
          const err = error as Error;
          logger.logError({
            message: `Hotjar failed to initialize:`,
            properties: {
              name: err.name,
              message: err.message,
              stack: err.stack || "unknown stack",
              cause: String(err.cause ?? "unknown cause")
            }
          });
        }
      }

      const userId = idTokenClaims?.userIdent as string;
      const tenantName = idTokenClaims.tenant_name as string;
      const userName = idTokenClaims?.user_name as string;
      const isKamstrupUser = userName?.toLowerCase().includes("@kamstrup");

      if (userId && siteId) {
        try {
          Hotjar.identify(userId, { tenant_name: tenantName, isKamstrupUser: isKamstrupUser });
        } catch (error) {
          const err = error as Error;
          logger.logError({
            message: `Hotjar failed to identify:`,
            properties: {
              name: err.name,
              message: err.message,
              stack: err.stack || "unknown stack",
              cause: String(err.cause ?? "unknown cause")
            }
          });
        }
      }
    }
  }, [environment, idTokenClaims]);

 
    return (
      <ThemeProvider theme={theme}>
        <div className="main">
          <RouterProvider router={createBrowserRouter([routes], { basename: getEnv().PUBLIC_URL })} />
        </div>
      </ThemeProvider>
    );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider config={authConfig} errorPage={AuthErrorComponent}>
          <TranslationsProvider
            translations={translations}
            developerMessagesFallback={developerTranslationFile}
          >
            <DashboardModule />
          </TranslationsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

